// v2
// Path: frontend/src/components/QuickBooksSync/FieldMappingDesigner.jsx
// Inputs: mode ('pipeline' | 'standard'), inline (bool), token, config, standardMapping, onSave, onClose, onResetDefaults, onSaved, onPipelineUpdated
// Outputs: Reusable unified field mapping designer, inspect/diff analyzer, and live sync execution component
// Summary: Core unified pipeline interface supporting field mapping design, real-time inspect & diff comparisons against live QuickBooks, and direct sync execution, with standalone standard preset mode.

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import SearchableSelect from './SearchableSelect';
import { getQbEntityMeta, normalizeEntityName } from './quickbooksEntities';

// Helper to merge target fields (official + discovered custom) with existing mapping assignments
const mergeTargetFieldsWithMappings = (tFields, cFields, rawMappings) => {
    const allTargets = [...(tFields || [])];
    (cFields || []).forEach(cf => {
        if (!allTargets.some(t => t.name.toLowerCase() === cf.name.toLowerCase())) {
            allTargets.push(cf);
        }
    });

    const existingMap = new Map();
    (rawMappings || []).forEach(m => {
        if (m && m.target) {
            existingMap.set(m.target.toLowerCase(), {
                target: m.target,
                sources: Array.isArray(m.sources) && m.sources.length > 0 
                    ? m.sources 
                    : (m.source ? (m.source.includes('+') ? m.source.split('+').map(s => s.trim()) : [m.source]) : []),
                source: m.source || (Array.isArray(m.sources) ? m.sources.join(m.delimiter !== undefined ? m.delimiter : ' - ') : ''),
                delimiter: m.delimiter !== undefined ? m.delimiter : ' - ',
                template: m.template || ''
            });
        }
    });

    const result = [];
    // 1. All known target fields in canonical order
    for (const tf of allTargets) {
        const lower = tf.name.toLowerCase();
        if (existingMap.has(lower)) {
            result.push(existingMap.get(lower));
            existingMap.delete(lower);
        } else {
            result.push({
                target: tf.name,
                sources: [],
                source: '',
                delimiter: ' - ',
                template: ''
            });
        }
    }

    // 2. Any custom mappings previously added not present in target fields list
    for (const remaining of existingMap.values()) {
        result.push(remaining);
    }

    return result;
};

// Helper to extract nested live sample values from QuickBooks sample records
const getLiveQbSampleValue = (fieldPath, sample) => {
    if (!sample) return '(No sample)';
    if (!fieldPath) return '';
    const parts = fieldPath.split('.');
    let val = sample;
    for (const p of parts) {
        if (val === null || val === undefined) return '(Empty in sample)';
        val = val[p];
    }
    if (val === null || val === undefined || val === '') return '(Empty in sample)';
    if (typeof val === 'object') {
        return JSON.stringify(val);
    }
    return String(val);
};

export const FieldMappingDesigner = ({
    mode = 'pipeline', // 'pipeline' or 'standard'
    inline = false,
    token,
    config = {},
    standardMapping = {},
    onSave,
    onSaved,
    onClose,
    onResetDefaults,
    onPipelineUpdated
}) => {
    const isStandardMode = mode === 'standard';

    // Sub-view Tab for Pipeline Mode: 'mappings' | 'diff' | 'sync'
    const [pipelineTab, setPipelineTab] = useState('mappings');

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [saveMessage, setSaveMessage] = useState(null);
    const [definitions, setDefinitions] = useState(null);
    const [mappings, setMappings] = useState([]);
    const [searchFilter, setSearchFilter] = useState('');

    // Active Entity and Target QuickBooks Entity
    const initialEntity = isStandardMode 
        ? normalizeEntityName(standardMapping.EntityType || standardMapping.TargetQBEntity || 'Customer')
        : normalizeEntityName(config.TargetQBEntity || config.EntityType || 'Customer');
    const [selectedEntity, setSelectedEntity] = useState(initialEntity);

    const initialQBEntity = isStandardMode 
        ? normalizeEntityName(standardMapping.TargetQBEntity || standardMapping.EntityType || 'Customer')
        : normalizeEntityName(config.TargetQBEntity || config.EntityType || 'Customer');
    const [selectedQBEntity, setSelectedQBEntity] = useState(initialQBEntity);

    // Source Table State
    const meta = getQbEntityMeta(selectedQBEntity);
    const initialTable = isStandardMode 
        ? (standardMapping.SourceTable || meta.defaultTable || '') 
        : (config.TableName || meta.defaultTable || '');
    const [selectedTable, setSelectedTable] = useState(initialTable);
    const [availableTables, setAvailableTables] = useState([]);
    const [loadingTables, setLoadingTables] = useState(false);

    // QuickBooks Custom Fields
    const [qbCustomFields, setQbCustomFields] = useState([]);
    const [loadingCustomFields, setLoadingCustomFields] = useState(false);

    // Live Samples
    const [sampleRecords, setSampleRecords] = useState([]);
    const [activeSampleIndex, setActiveSampleIndex] = useState(0);
    const [loadingSamples, setLoadingSamples] = useState(false);

    // Live QuickBooks Samples
    const [qbSampleRecords, setQbSampleRecords] = useState([]);
    const [activeQbSampleIndex, setActiveQbSampleIndex] = useState(0);
    const [loadingQbSamples, setLoadingQbSamples] = useState(false);

    // Add Custom Mapping State
    const [newTarget, setNewTarget] = useState('');
    const [newSource, setNewSource] = useState('');

    // Pipeline Active Toggle State
    const [isActive, setIsActive] = useState(config.IsActive !== false);

    // Inspect & Diff State
    const [diffLimit, setDiffLimit] = useState(50);
    const [loadingDiff, setLoadingDiff] = useState(false);
    const [diffPreviewData, setDiffPreviewData] = useState(null);
    const [diffError, setDiffError] = useState(null);
    const [diffDetailTab, setDiffDetailTab] = useState('changes'); // 'changes' | 'qb_sample' | 'pbs_sample'

    // Sync Execution State
    const [syncLimit, setSyncLimit] = useState(50);
    const [loadingSync, setLoadingSync] = useState(false);
    const [syncResult, setSyncResult] = useState(null);
    const [showSyncConfirm, setShowSyncConfirm] = useState(false);

    // Fetch live tables from the source database profile
    useEffect(() => {
        const fetchDatabaseTables = async () => {
            const connPrefix = config.SourceConnPrefix || standardMapping.SourceConnPrefix;
            const dbName = config.SourceDatabaseName || standardMapping.SourceDatabaseName || 'pbsdata00';
            if (!connPrefix && isStandardMode) {
                try {
                    const dictRes = await fetch('/api/quickbooks/data-dictionary/tables', {
                        headers: { 'Authorization': `Bearer ${token}` }
                    });
                    if (dictRes.ok) {
                        const tables = await dictRes.json();
                        setAvailableTables(tables.map(t => ({ name: t.TableName, description: t.Description, module: t.Module })));
                    }
                } catch (e) {}
                return;
            }

            setLoadingTables(true);
            try {
                const params = new URLSearchParams();
                if (connPrefix) params.append('connPrefix', connPrefix);
                if (dbName) params.append('databaseName', dbName);

                const res = await fetch(`/api/quickbooks/database-tables?${params.toString()}`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                if (res.ok) {
                    const data = await res.json();
                    if (Array.isArray(data)) {
                        setAvailableTables(data.map(t => ({ name: t.name || t.TABLE_NAME || t, description: t.description, module: t.module })));
                    }
                }
            } catch (err) {
                console.warn('[FieldMappingDesigner] Could not fetch database tables:', err);
            } finally {
                setLoadingTables(false);
            }
        };

        fetchDatabaseTables();
    }, [token, config.SourceConnPrefix, config.SourceDatabaseName, isStandardMode]);

    // Active table name fallback
    const activeTableName = useMemo(() => {
        if (selectedTable && selectedTable.trim()) return selectedTable.trim();
        if (config.TableName && config.TableName.trim()) return config.TableName.trim();
        if (standardMapping.SourceTable && standardMapping.SourceTable.trim()) return standardMapping.SourceTable.trim();
        return meta.defaultTable || 'CUSFIL';
    }, [selectedTable, config.TableName, standardMapping.SourceTable, meta.defaultTable]);

    // Fetch Field Definitions (ERP columns & QB target fields)
    const fetchDefinitions = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams({
                entityType: selectedEntity,
                sourceConnPrefix: config.SourceConnPrefix || '',
                sourceDatabaseName: config.SourceDatabaseName || 'pbsdata00',
                tableName: activeTableName
            });

            const res = await fetch(`/api/quickbooks/field-definitions?${params.toString()}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (!res.ok) throw new Error('Failed to load field definitions');
            const data = await res.json();
            setDefinitions(data);

            // Raw initial mappings
            let initialRaw = [];
            if (isStandardMode) {
                if (standardMapping.FieldMappingsJson) {
                    try {
                        const parsed = typeof standardMapping.FieldMappingsJson === 'string'
                            ? JSON.parse(standardMapping.FieldMappingsJson)
                            : standardMapping.FieldMappingsJson;
                        if (Array.isArray(parsed)) initialRaw = parsed;
                    } catch (e) {}
                } else if (Array.isArray(standardMapping.mappings)) {
                    initialRaw = standardMapping.mappings;
                }
            } else {
                if (config.FieldMappingsJson || config.FieldMappings) {
                    try {
                        const parsed = typeof (config.FieldMappingsJson || config.FieldMappings) === 'string'
                            ? JSON.parse(config.FieldMappingsJson || config.FieldMappings)
                            : (config.FieldMappingsJson || config.FieldMappings);
                        if (Array.isArray(parsed)) initialRaw = parsed;
                    } catch (e) {}
                }
            }

            const targetFields = data.targetFields || [];
            const merged = mergeTargetFieldsWithMappings(targetFields, qbCustomFields, initialRaw);
            setMappings(merged);
        } catch (err) {
            console.error('[FieldMappingDesigner] Error fetching definitions:', err);
            setSaveMessage({ type: 'error', text: err.message });
        } finally {
            setLoading(false);
        }
    }, [selectedEntity, config.SourceConnPrefix, config.SourceDatabaseName, activeTableName, token, isStandardMode, standardMapping, config, qbCustomFields]);

    useEffect(() => {
        fetchDefinitions();
    }, [fetchDefinitions]);

    // Fetch Live Samples from PBS ERP source
    const fetchSamples = useCallback(async () => {
        if (!activeTableName) return;
        setLoadingSamples(true);
        try {
            const params = new URLSearchParams({
                sourceConnPrefix: config.SourceConnPrefix || '',
                sourceDatabaseName: config.SourceDatabaseName || 'pbsdata00',
                tableName: activeTableName,
                entityType: selectedEntity,
                limit: '10'
            });

            const res = await fetch(`/api/quickbooks/pbs-preview?${params.toString()}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (res.ok) {
                const data = await res.json();
                if (Array.isArray(data.sampleRecords) && data.sampleRecords.length > 0) {
                    setSampleRecords(data.sampleRecords);
                    setActiveSampleIndex(0);
                } else {
                    setSampleRecords([]);
                }
            }
        } catch (err) {
            console.warn('[FieldMappingDesigner] Could not fetch PBS samples:', err);
        } finally {
            setLoadingSamples(false);
        }
    }, [activeTableName, config.SourceConnPrefix, config.SourceDatabaseName, selectedEntity, token]);

    // Fetch Live Destination Samples from QuickBooks Online
    const fetchQbSamples = useCallback(async () => {
        const accountId = config.AccountID || standardMapping.AccountID;
        if (!accountId) return;

        setLoadingQbSamples(true);
        try {
            const params = new URLSearchParams({
                accountId: String(accountId),
                entity: selectedQBEntity,
                limit: '10'
            });

            const res = await fetch(`/api/quickbooks/qb-preview?${params.toString()}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (res.ok) {
                const data = await res.json();
                if (Array.isArray(data.sampleRecords) && data.sampleRecords.length > 0) {
                    setQbSampleRecords(data.sampleRecords);
                    setActiveQbSampleIndex(0);
                } else {
                    setQbSampleRecords([]);
                }
            }
        } catch (err) {
            console.warn('[FieldMappingDesigner] Could not fetch QB samples:', err);
        } finally {
            setLoadingQbSamples(false);
        }
    }, [config.AccountID, standardMapping.AccountID, selectedQBEntity, token]);

    useEffect(() => {
        fetchSamples();
        fetchQbSamples();
    }, [fetchSamples, fetchQbSamples]);

    // Active Samples
    const activeSample = sampleRecords[activeSampleIndex] || null;
    const activeQbSample = qbSampleRecords[activeQbSampleIndex] || null;

    // Source Columns Options
    const sourceColumns = useMemo(() => {
        return definitions?.sourceFields || [];
    }, [definitions]);

    const sourceSelectOptions = useMemo(() => {
        return sourceColumns.map(col => ({
            value: col.name,
            label: `${col.name}${col.description ? ` (${col.description})` : ''}`,
            subText: `${col.type}${col.isPrimaryKey ? ' • PK' : ''}`
        }));
    }, [sourceColumns]);

    // Target Fields Options
    const targetSelectOptions = useMemo(() => {
        const official = (definitions?.targetFields || []).map(f => ({
            value: f.name,
            label: `${f.name}${f.description ? ` (${f.description})` : ''}`,
            subText: f.type || 'String'
        }));
        const custom = qbCustomFields.map(f => ({
            value: f.name,
            label: `${f.name} (Custom Field)`,
            subText: f.type || 'Custom String'
        }));
        return [...official, ...custom];
    }, [definitions, qbCustomFields]);

    // Filtered Mappings List
    const filteredMappings = useMemo(() => {
        if (!searchFilter.trim()) return mappings;
        const q = searchFilter.toLowerCase();
        return mappings.filter(m => 
            (m.target && m.target.toLowerCase().includes(q)) ||
            (m.source && m.source.toLowerCase().includes(q)) ||
            (Array.isArray(m.sources) && m.sources.some(s => s.toLowerCase().includes(q)))
        );
    }, [mappings, searchFilter]);

    // Multi-source builder actions
    const handleAddSourceColumn = (targetName, colName) => {
        if (!colName) return;
        setMappings(prev => prev.map(m => {
            if (m.target.toLowerCase() === targetName.toLowerCase()) {
                const curSources = Array.isArray(m.sources) ? m.sources : (m.source ? [m.source] : []);
                if (curSources.includes(colName)) return m;
                const nextSources = [...curSources, colName];
                const delim = m.delimiter !== undefined ? m.delimiter : ' - ';
                return {
                    ...m,
                    sources: nextSources,
                    source: nextSources.join(delim)
                };
            }
            return m;
        }));
    };

    const handleRemoveSourceColumn = (targetName, colIndex) => {
        setMappings(prev => prev.map(m => {
            if (m.target.toLowerCase() === targetName.toLowerCase()) {
                const curSources = Array.isArray(m.sources) ? m.sources : (m.source ? [m.source] : []);
                const nextSources = curSources.filter((_, idx) => idx !== colIndex);
                const delim = m.delimiter !== undefined ? m.delimiter : ' - ';
                return {
                    ...m,
                    sources: nextSources,
                    source: nextSources.join(delim)
                };
            }
            return m;
        }));
    };

    const handleUpdateDelimiter = (targetName, newDelimiter) => {
        setMappings(prev => prev.map(m => {
            if (m.target.toLowerCase() === targetName.toLowerCase()) {
                const curSources = Array.isArray(m.sources) ? m.sources : (m.source ? [m.source] : []);
                return {
                    ...m,
                    delimiter: newDelimiter,
                    source: curSources.join(newDelimiter)
                };
            }
            return m;
        }));
    };

    const handleUnassignSource = (targetName) => {
        setMappings(prev => prev.map(m => {
            if (m.target.toLowerCase() === targetName.toLowerCase()) {
                return {
                    ...m,
                    sources: [],
                    source: '',
                    template: ''
                };
            }
            return m;
        }));
    };

    const handleAddCustomMapping = () => {
        if (!newTarget.trim() || !newSource.trim()) return;
        const exists = mappings.some(m => m.target.toLowerCase() === newTarget.trim().toLowerCase());
        if (exists) {
            alert(`Target field "${newTarget.trim()}" is already present in the mappings list.`);
            return;
        }

        const newEntry = {
            target: newTarget.trim(),
            sources: [newSource.trim()],
            source: newSource.trim(),
            delimiter: ' - ',
            template: ''
        };

        setMappings(prev => [newEntry, ...prev]);
        setNewTarget('');
        setNewSource('');
    };

    const handleRemoveCustomRow = (targetName) => {
        setMappings(prev => prev.filter(m => m.target.toLowerCase() !== targetName.toLowerCase()));
    };

    // Load standard preset into current editor
    const handleLoadStandardPreset = async () => {
        if (!window.confirm(`Overwrite current mappings with standard presets for ${selectedEntity}?`)) return;
        setLoading(true);
        try {
            const res = await fetch(`/api/quickbooks/standard-mappings?sourceSystem=Passport`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                const data = await res.json();
                const std = (data.mappings || []).find(m => 
                    (m.TargetQBEntity || m.EntityType || '').toLowerCase() === selectedQBEntity.toLowerCase()
                );
                if (std && std.mappings) {
                    const targetFields = definitions?.targetFields || [];
                    const merged = mergeTargetFieldsWithMappings(targetFields, qbCustomFields, std.mappings);
                    setMappings(merged);
                    setSaveMessage({ type: 'success', text: 'Standard preset loaded into designer!' });
                } else {
                    alert(`No standard mapping preset found for entity "${selectedQBEntity}".`);
                }
            }
        } catch (err) {
            alert(`Failed to load standard preset: ${err.message}`);
        } finally {
            setLoading(false);
        }
    };

    // Toggle active state for pipeline
    const handleToggleActive = async (checked) => {
        setIsActive(checked);
        if (!config.ConfigID) return;
        try {
            await fetch('/api/quickbooks/configs/toggle-active', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ configId: config.ConfigID, isActive: checked })
            });
            if (onPipelineUpdated) onPipelineUpdated();
        } catch (e) {
            console.error('Failed to toggle active state:', e);
        }
    };

    // Save field mappings
    const handleSave = async (e) => {
        if (e) e.preventDefault();
        setSaving(true);
        setSaveMessage(null);
        try {
            const cleanMappings = mappings
                .filter(m => m.target && ((Array.isArray(m.sources) && m.sources.length > 0) || (m.source && m.source.trim().length > 0) || (m.template && m.template.trim().length > 0)))
                .map(m => ({
                    target: m.target.trim(),
                    source: m.source ? m.source.trim() : (m.sources ? m.sources.join(m.delimiter || ' - ') : ''),
                    sources: Array.isArray(m.sources) && m.sources.length > 0 ? m.sources : [(m.source || '').trim()],
                    delimiter: m.delimiter !== undefined ? m.delimiter : ' - ',
                    template: m.template || undefined
                }));

            if (isStandardMode) {
                if (onSave) {
                    await onSave({
                        sourceTable: activeTableName,
                        targetQBEntity: selectedQBEntity,
                        mappings: cleanMappings
                    });
                }
                setSaveMessage({ type: 'success', text: 'Standard mapping preset saved successfully!' });
            } else {
                const payload = {
                    configId: config.ConfigID,
                    tableName: activeTableName,
                    targetQBEntity: selectedQBEntity,
                    fieldMappingsJson: JSON.stringify(cleanMappings)
                };
                const res = await fetch('/api/quickbooks/configs/field-mappings', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify(payload)
                });
                if (!res.ok) {
                    const err = await res.json();
                    throw new Error(err.error || 'Failed to save field mappings');
                }
                setSaveMessage({ type: 'success', text: `Field mappings for ${config.ConfigName || activeTableName} saved!` });
                if (onSaved) onSaved();
                if (onPipelineUpdated) onPipelineUpdated();
            }
        } catch (err) {
            setSaveMessage({ type: 'error', text: err.message });
        } finally {
            setSaving(false);
        }
    };

    // Run Inspect & Diff Analysis
    const handleRunDiff = async () => {
        setLoadingDiff(true);
        setDiffError(null);
        try {
            const payload = {
                configId: config.ConfigID,
                accountId: config.AccountID,
                sourceConnPrefix: config.SourceConnPrefix,
                sourceDatabaseName: config.SourceDatabaseName,
                tableName: activeTableName,
                targetQBEntity: selectedQBEntity,
                entityType: config.EntityType || selectedEntity,
                limit: diffLimit
            };

            const res = await fetch('/api/quickbooks/sync/preview', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(payload)
            });

            if (!res.ok) {
                const err = await res.json();
                throw new Error(err.error || 'Dry run diff analysis failed');
            }

            const data = await res.json();
            setDiffPreviewData(data);
        } catch (err) {
            console.error('Diff error:', err);
            setDiffError(err.message);
        } finally {
            setLoadingDiff(false);
        }
    };

    // Execute Live Batch Sync
    const handleExecuteLiveSync = async (isDryRun = false) => {
        setLoadingSync(true);
        setSyncResult(null);
        setShowSyncConfirm(false);
        try {
            const endpoint = isDryRun ? '/api/quickbooks/sync/preview' : '/api/quickbooks/sync/execute';
            const payload = {
                configId: config.ConfigID,
                accountId: config.AccountID,
                sourceConnPrefix: config.SourceConnPrefix,
                sourceDatabaseName: config.SourceDatabaseName,
                tableName: activeTableName,
                targetQBEntity: selectedQBEntity,
                entityType: config.EntityType || selectedEntity,
                limit: syncLimit
            };

            const res = await fetch(endpoint, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(payload)
            });

            if (!res.ok) {
                const err = await res.json();
                throw new Error(err.error || 'Sync execution failed');
            }

            const data = await res.json();
            setSyncResult(data);
            if (onSaved) onSaved();
            if (onPipelineUpdated) onPipelineUpdated();
        } catch (err) {
            alert(`Execution Error: ${err.message}`);
        } finally {
            setLoadingSync(false);
        }
    };

    const activeMappedCount = mappings.filter(m => (m.sources && m.sources.length > 0) || (m.source && m.source.trim())).length;

    return (
        <div className={`flex flex-col bg-slate-900 text-slate-200 ${inline ? 'w-full rounded-xl border border-slate-750 shadow-xl overflow-hidden' : 'h-full overflow-hidden'}`}>
            {/* Top Identity & Action Header */}
            <div className="p-4 bg-slate-850 border-b border-slate-700/80 flex flex-wrap items-center justify-between gap-3 shrink-0">
                {/* Left: Identity */}
                <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-lg bg-${meta.color || 'indigo'}-500/10 border border-${meta.color || 'indigo'}-500/20 flex items-center justify-center text-${meta.color || 'indigo'}-400`}>
                        <span className="material-symbols-outlined text-[24px]">
                            {meta.icon || 'tune'}
                        </span>
                    </div>
                    <div>
                        <div className="flex items-center gap-2">
                            <h3 className="text-base font-bold text-white tracking-wide">
                                {isStandardMode ? `Standard Preset: ${selectedQBEntity}` : (config.ConfigName || `PBS ${selectedQBEntity} -> QBO`)}
                            </h3>
                            <span className="px-2 py-0.5 text-[10px] uppercase font-bold tracking-wider rounded bg-indigo-500/20 text-indigo-300 border border-indigo-500/30">
                                {isStandardMode ? 'Global Preset' : 'Pipeline'}
                            </span>
                            {!isStandardMode && (
                                <span className="px-2 py-0.5 text-[10px] font-bold rounded bg-emerald-950/80 text-emerald-300 border border-emerald-800">
                                    {activeMappedCount} Mapped
                                </span>
                            )}
                        </div>
                        <p className="text-xs text-slate-400">
                            {isStandardMode 
                                ? 'Configure canonical field mappings for source ERP tables.'
                                : `Syncing ${config.SourceDatabaseName || 'PBS'} ➔ QuickBooks ${selectedQBEntity}.`}
                        </p>
                    </div>
                </div>

                {/* Center / Right: Sub-View Tabs (Pipeline Mode Only) */}
                {!isStandardMode && (
                    <div className="flex items-center gap-2">
                        {/* Inline Enablement Toggle */}
                        <label className="flex items-center gap-1.5 cursor-pointer select-none bg-slate-900 px-3 py-1.5 rounded-lg border border-slate-750 text-xs font-semibold hover:border-slate-650 transition-colors">
                            <input
                                type="checkbox"
                                checked={isActive}
                                onChange={e => handleToggleActive(e.target.checked)}
                                className="rounded bg-slate-800 border-slate-700 text-emerald-500 focus:ring-0 cursor-pointer"
                            />
                            <span className={isActive ? 'text-emerald-300 font-bold' : 'text-slate-400'}>
                                {isActive ? 'Enabled for Sync' : 'Disabled'}
                            </span>
                        </label>

                        {/* Mode Switcher Tabs */}
                        <div className="flex bg-slate-900 p-1 rounded-xl border border-slate-750 gap-1">
                            <button
                                type="button"
                                onClick={() => setActiveTab('mappings')}
                                className={`px-3 py-1 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 ${
                                    pipelineTab === 'mappings'
                                        ? 'bg-indigo-600 text-white shadow-sm'
                                        : 'text-slate-400 hover:text-white hover:bg-slate-800'
                                }`}
                            >
                                <span className="material-symbols-outlined text-[15px]">alt_route</span>
                                Field Mappings
                            </button>
                            <button
                                type="button"
                                onClick={() => {
                                    setPipelineTab('diff');
                                    if (!diffPreviewData) handleRunDiff();
                                }}
                                className={`px-3 py-1 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 ${
                                    pipelineTab === 'diff'
                                        ? 'bg-blue-600 text-white shadow-sm'
                                        : 'text-slate-400 hover:text-white hover:bg-slate-800'
                                }`}
                            >
                                <span className="material-symbols-outlined text-[15px]">compare_arrows</span>
                                Inspect & Diff
                            </button>
                            <button
                                type="button"
                                onClick={() => setPipelineTab('sync')}
                                className={`px-3 py-1 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 ${
                                    pipelineTab === 'sync'
                                        ? 'bg-emerald-600 text-white shadow-sm'
                                        : 'text-slate-400 hover:text-white hover:bg-slate-800'
                                }`}
                            >
                                <span className="material-symbols-outlined text-[15px]">sync</span>
                                Sync Execution
                            </button>
                        </div>
                    </div>
                )}

                {/* Right: Quick Actions */}
                <div className="flex items-center gap-2">
                    {!isStandardMode && (
                        <button
                            type="button"
                            onClick={handleLoadStandardPreset}
                            className="px-2.5 py-1.5 bg-indigo-900/50 hover:bg-indigo-800/70 text-indigo-200 border border-indigo-700/50 rounded-lg text-xs font-semibold flex items-center gap-1 transition-colors"
                            title="Reset to canonical standard mapping preset"
                        >
                            <span className="material-symbols-outlined text-[14px] text-emerald-400">bolt</span>
                            Apply Standards
                        </button>
                    )}
                    {isStandardMode && onResetDefaults && (
                        <button
                            type="button"
                            onClick={onResetDefaults}
                            className="px-3 py-1.5 bg-slate-800 hover:bg-slate-700 text-slate-300 border border-slate-700 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition-colors"
                        >
                            <span className="material-symbols-outlined text-[15px]">restart_alt</span>
                            Reset Defaults
                        </button>
                    )}
                    {onClose && (
                        <button
                            type="button"
                            onClick={onClose}
                            className="p-1.5 text-slate-400 hover:text-white rounded-lg hover:bg-slate-800 transition-colors"
                            title="Close / Collapse"
                        >
                            <span className="material-symbols-outlined text-[20px]">close</span>
                        </button>
                    )}
                </div>
            </div>

            {/* Notification Bar */}
            {saveMessage && (
                <div className={`px-4 py-2 text-xs flex items-center justify-between border-b ${saveMessage.type === 'success' ? 'bg-emerald-950/60 border-emerald-800 text-emerald-300' : 'bg-rose-950/60 border-rose-800 text-rose-300'}`}>
                    <div className="flex items-center gap-2">
                        <span className="material-symbols-outlined text-[16px]">
                            {saveMessage.type === 'success' ? 'check_circle' : 'error'}
                        </span>
                        <span>{saveMessage.text}</span>
                    </div>
                    <button onClick={() => setSaveMessage(null)} className="text-slate-400 hover:text-white text-xs">&times;</button>
                </div>
            )}

            {/* ════════════════════════════════════════════════════════════════════════════
                TAB 1: FIELD MAPPINGS DESIGNER
            ════════════════════════════════════════════════════════════════════════════ */}
            {pipelineTab === 'mappings' && (
                <div className="flex flex-col flex-1 overflow-hidden">
                    {/* Source Table Selector Bar */}
                    <div className="px-4 py-2.5 bg-slate-900 border-b border-slate-800 flex flex-wrap items-center justify-between gap-3 text-xs">
                        <div className="flex items-center gap-2.5">
                            <span className="text-slate-400 font-bold flex items-center gap-1">
                                <span className="material-symbols-outlined text-[15px] text-sky-400">table_chart</span>
                                ERP Source Table:
                            </span>
                            <input
                                type="text"
                                value={selectedTable}
                                onChange={e => setSelectedTable(e.target.value.toUpperCase())}
                                placeholder={activeTableName}
                                className="bg-slate-950 border border-slate-700 rounded px-2.5 py-1 text-white font-mono text-xs focus:border-sky-500 focus:outline-none w-36 uppercase"
                                title="Type custom or select table"
                            />
                            {availableTables.length > 0 && (
                                <select
                                    value={selectedTable}
                                    onChange={e => setSelectedTable(e.target.value)}
                                    className="bg-slate-950 border border-slate-700 rounded px-2.5 py-1 text-slate-300 text-xs focus:outline-none"
                                >
                                    <option value="">(Select from schema...)</option>
                                    {availableTables.map(t => (
                                        <option key={t.name} value={t.name}>
                                            {t.name} {t.module ? `(${t.module})` : ''}
                                        </option>
                                    ))}
                                </select>
                            )}
                            <span className="text-slate-600">&bull;</span>
                            <span className="text-slate-400">
                                Total Available Columns: <strong className="text-slate-200">{sourceColumns.length}</strong>
                            </span>
                        </div>

                        <div className="flex items-center gap-2">
                            <span className="text-slate-400">Search Mappings:</span>
                            <input
                                type="text"
                                value={searchFilter}
                                onChange={e => setSearchFilter(e.target.value)}
                                placeholder="Filter target or source..."
                                className="bg-slate-950 border border-slate-700 rounded px-2.5 py-1 text-white text-xs focus:border-indigo-500 focus:outline-none w-48"
                            />
                        </div>
                    </div>

                    {/* Live Sample Navigators (Reversed order: QB on Left, PBS on Right) */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 p-4 bg-slate-950/60 border-b border-slate-800 shrink-0 text-xs">
                        {/* LEFT: QuickBooks Destination Live Sample */}
                        <div className="bg-slate-900/90 border border-purple-900/50 rounded-xl p-3 space-y-2">
                            <div className="flex justify-between items-center">
                                <div className="flex items-center gap-1.5 font-bold text-purple-300">
                                    <span className="material-symbols-outlined text-[16px]">cloud</span>
                                    <span>QuickBooks Destination Sample ({selectedQBEntity})</span>
                                </div>
                                <div className="flex items-center gap-1">
                                    <span className="text-[11px] text-slate-400 mr-2 font-mono">
                                        {qbSampleRecords.length > 0 ? `${activeQbSampleIndex + 1} of ${qbSampleRecords.length}` : '0 records'}
                                    </span>
                                    <button
                                        type="button"
                                        onClick={() => setActiveQbSampleIndex(0)}
                                        disabled={activeQbSampleIndex === 0 || qbSampleRecords.length === 0}
                                        className="px-1.5 py-0.5 rounded bg-slate-800 hover:bg-slate-700 disabled:opacity-40 text-slate-300"
                                        title="First Record"
                                    >&laquo;</button>
                                    <button
                                        type="button"
                                        onClick={() => setActiveQbSampleIndex(prev => Math.max(0, prev - 1))}
                                        disabled={activeQbSampleIndex === 0}
                                        className="px-2 py-0.5 rounded bg-slate-800 hover:bg-slate-700 disabled:opacity-40 text-slate-300"
                                        title="Previous Record"
                                    >&lsaquo;</button>
                                    <button
                                        type="button"
                                        onClick={() => setActiveQbSampleIndex(prev => Math.min(qbSampleRecords.length - 1, prev + 1))}
                                        disabled={activeQbSampleIndex >= qbSampleRecords.length - 1}
                                        className="px-2 py-0.5 rounded bg-slate-800 hover:bg-slate-700 disabled:opacity-40 text-slate-300"
                                        title="Next Record"
                                    >&rsaquo;</button>
                                    <button
                                        type="button"
                                        onClick={() => setActiveQbSampleIndex(qbSampleRecords.length - 1)}
                                        disabled={activeQbSampleIndex >= qbSampleRecords.length - 1 || qbSampleRecords.length === 0}
                                        className="px-1.5 py-0.5 rounded bg-slate-800 hover:bg-slate-700 disabled:opacity-40 text-slate-300"
                                        title="Last Record"
                                    >&raquo;</button>
                                </div>
                            </div>
                            <div className="bg-slate-950 p-2 rounded-lg font-mono text-[11px] text-purple-200 border border-purple-950/80 truncate">
                                {loadingQbSamples ? (
                                    <span className="text-slate-500">Querying live QuickBooks destination records...</span>
                                ) : activeQbSample ? (
                                    <span>
                                        <strong>Id: {activeQbSample.Id}</strong> &bull; {activeQbSample.DisplayName || activeQbSample.Name || activeQbSample.DocNumber || '(Record)'}
                                    </span>
                                ) : (
                                    <span className="text-slate-500">No QuickBooks destination sample records found.</span>
                                )}
                            </div>
                        </div>

                        {/* RIGHT: PBS ERP Source Live Sample */}
                        <div className="bg-slate-900/90 border border-sky-900/50 rounded-xl p-3 space-y-2">
                            <div className="flex justify-between items-center">
                                <div className="flex items-center gap-1.5 font-bold text-sky-300">
                                    <span className="material-symbols-outlined text-[16px]">database</span>
                                    <span>ERP Source Sample ({activeTableName})</span>
                                </div>
                                <div className="flex items-center gap-1">
                                    <span className="text-[11px] text-slate-400 mr-2 font-mono">
                                        {sampleRecords.length > 0 ? `${activeSampleIndex + 1} of ${sampleRecords.length}` : '0 records'}
                                    </span>
                                    <button
                                        type="button"
                                        onClick={() => setActiveSampleIndex(0)}
                                        disabled={activeSampleIndex === 0 || sampleRecords.length === 0}
                                        className="px-1.5 py-0.5 rounded bg-slate-800 hover:bg-slate-700 disabled:opacity-40 text-slate-300"
                                        title="First Record"
                                    >&laquo;</button>
                                    <button
                                        type="button"
                                        onClick={() => setActiveSampleIndex(prev => Math.max(0, prev - 1))}
                                        disabled={activeSampleIndex === 0}
                                        className="px-2 py-0.5 rounded bg-slate-800 hover:bg-slate-700 disabled:opacity-40 text-slate-300"
                                        title="Previous Record"
                                    >&lsaquo;</button>
                                    <button
                                        type="button"
                                        onClick={() => setActiveSampleIndex(prev => Math.min(sampleRecords.length - 1, prev + 1))}
                                        disabled={activeSampleIndex >= sampleRecords.length - 1}
                                        className="px-2 py-0.5 rounded bg-slate-800 hover:bg-slate-700 disabled:opacity-40 text-slate-300"
                                        title="Next Record"
                                    >&rsaquo;</button>
                                    <button
                                        type="button"
                                        onClick={() => setActiveSampleIndex(sampleRecords.length - 1)}
                                        disabled={activeSampleIndex >= sampleRecords.length - 1 || sampleRecords.length === 0}
                                        className="px-1.5 py-0.5 rounded bg-slate-800 hover:bg-slate-700 disabled:opacity-40 text-slate-300"
                                        title="Last Record"
                                    >&raquo;</button>
                                </div>
                            </div>
                            <div className="bg-slate-950 p-2 rounded-lg font-mono text-[11px] text-sky-200 border border-sky-950/80 truncate">
                                {loadingSamples ? (
                                    <span className="text-slate-500">Extracting live sample from {activeTableName}...</span>
                                ) : activeSample ? (
                                    <span>
                                        <strong>PK:</strong> {JSON.stringify(activeSample).slice(0, 80)}...
                                    </span>
                                ) : (
                                    <span className="text-slate-500">No ERP source sample records found in {activeTableName}.</span>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Mappings Table */}
                    <div className="flex-1 overflow-y-auto p-4 space-y-3 min-h-[300px]">
                        {loading ? (
                            <div className="py-16 text-center text-slate-400 text-xs">
                                <span className="material-symbols-outlined animate-spin text-3xl mb-2 text-indigo-400">sync</span>
                                <p>Loading fields and schema definitions...</p>
                            </div>
                        ) : filteredMappings.length === 0 ? (
                            <div className="py-16 text-center text-slate-400 text-xs">
                                No mapping fields match filter "{searchFilter}".
                            </div>
                        ) : (
                            <div className="border border-slate-700/80 rounded-xl overflow-hidden bg-slate-850 shadow-md">
                                <table className="w-full text-left text-xs border-collapse">
                                    <thead>
                                        <tr className="bg-slate-800 text-slate-300 border-b border-slate-700">
                                            <th className="p-3 font-semibold w-1/3">
                                                <div className="flex items-center gap-1.5 text-purple-300 font-bold">
                                                    <span className="material-symbols-outlined text-[15px]">cloud</span>
                                                    QuickBooks Target Field (Canonical / Read-Only)
                                                </div>
                                            </th>
                                            <th className="p-3 font-semibold w-7/12">
                                                <div className="flex items-center gap-1.5 text-sky-300 font-bold">
                                                    <span className="material-symbols-outlined text-[15px]">database</span>
                                                    Source Column(s) & Delimiter Formatting ({activeTableName})
                                                </div>
                                            </th>
                                            <th className="p-3 font-semibold w-16 text-right">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-700/60">
                                        {filteredMappings.map(m => {
                                            const isMapped = (Array.isArray(m.sources) && m.sources.length > 0) || (m.source && m.source.trim().length > 0);
                                            const curSources = Array.isArray(m.sources) && m.sources.length > 0 ? m.sources : (m.source ? [m.source] : []);
                                            const liveQbVal = activeQbSample ? getLiveQbSampleValue(m.target, activeQbSample) : null;

                                            // Evaluated sample value from source
                                            let evalSourceVal = null;
                                            if (activeSample && curSources.length > 0) {
                                                evalSourceVal = curSources.map(s => {
                                                    const v = activeSample[s] !== undefined ? activeSample[s] : activeSample[s.toLowerCase()] || activeSample[s.toUpperCase()];
                                                    return v !== undefined && v !== null ? String(v).trim() : '';
                                                }).join(m.delimiter !== undefined ? m.delimiter : ' - ');
                                            }

                                            return (
                                                <tr key={m.target} className={`hover:bg-slate-800/80 transition-colors ${!isMapped ? 'opacity-70 bg-slate-900/30' : ''}`}>
                                                    {/* Target QB Field */}
                                                    <td className="p-3 align-top">
                                                        <div className="space-y-1">
                                                            <div className="flex items-center gap-2">
                                                                <span className="font-bold text-white font-mono text-xs">
                                                                    {m.target}
                                                                </span>
                                                                {m.isCustom && (
                                                                    <span className="px-1.5 py-0.2 text-[9px] rounded bg-purple-950 text-purple-300 border border-purple-800">
                                                                        Custom
                                                                    </span>
                                                                )}
                                                            </div>
                                                            {liveQbVal && (
                                                                <div className="text-[11px] font-mono text-purple-300 bg-purple-950/40 px-2 py-0.5 rounded border border-purple-900/50 truncate max-w-sm">
                                                                    <span className="text-purple-400/70 mr-1">QB:</span> {liveQbVal}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </td>

                                                    {/* Source Composite Multi-Column Builder */}
                                                    <td className="p-3 align-top">
                                                        <div className="space-y-2">
                                                            {/* Selected Source Chips */}
                                                            {curSources.length > 0 ? (
                                                                <div className="flex flex-wrap items-center gap-1.5">
                                                                    {curSources.map((s, idx) => (
                                                                        <span
                                                                            key={idx}
                                                                            className="px-2 py-1 rounded bg-sky-950 border border-sky-800 text-sky-200 font-mono text-xs flex items-center gap-1 shadow-sm"
                                                                        >
                                                                            <span>{s}</span>
                                                                            <button
                                                                                type="button"
                                                                                onClick={() => handleRemoveSourceColumn(m.target, idx)}
                                                                                className="text-sky-400 hover:text-white ml-0.5"
                                                                                title="Remove column"
                                                                            >
                                                                                &times;
                                                                            </button>
                                                                        </span>
                                                                    ))}

                                                                    {/* Custom Delimiter Input */}
                                                                    {curSources.length > 1 && (
                                                                        <div className="flex items-center gap-1 text-[11px] text-slate-400 ml-1">
                                                                            <span>Delimiter:</span>
                                                                            <input
                                                                                type="text"
                                                                                value={m.delimiter !== undefined ? m.delimiter : ' - '}
                                                                                onChange={e => handleUpdateDelimiter(m.target, e.target.value)}
                                                                                className="bg-slate-900 border border-slate-700 rounded px-1.5 py-0.5 text-white font-mono text-xs w-12 text-center"
                                                                                placeholder=" - "
                                                                            />
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            ) : (
                                                                <div className="text-[11px] text-slate-500 italic">
                                                                    Unmapped (Select column below to assign)
                                                                </div>
                                                            )}

                                                            {/* Searchable Select to Add Source Column */}
                                                            <div className="max-w-md">
                                                                <SearchableSelect
                                                                    options={sourceSelectOptions.filter(opt => !curSources.includes(opt.value))}
                                                                    value=""
                                                                    onChange={val => handleAddSourceColumn(m.target, val)}
                                                                    placeholder={curSources.length === 0 ? "+ Assign Source Column..." : "+ Add Concat Column..."}
                                                                    className="w-full"
                                                                />
                                                            </div>

                                                            {/* Evaluated Preview String */}
                                                            {evalSourceVal && (
                                                                <div className="text-[11px] font-mono text-emerald-300 bg-emerald-950/40 px-2 py-0.5 rounded border border-emerald-900/50 truncate max-w-md">
                                                                    <span className="text-emerald-400/70 mr-1">Sample Value:</span> {evalSourceVal}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </td>

                                                    {/* Actions */}
                                                    <td className="p-3 align-top text-right">
                                                        {isMapped ? (
                                                            <button
                                                                type="button"
                                                                onClick={() => handleUnassignSource(m.target)}
                                                                className="p-1.5 text-slate-400 hover:text-amber-400 rounded hover:bg-slate-800 transition-colors"
                                                                title="Unassign sources"
                                                            >
                                                                <span className="material-symbols-outlined text-[17px]">link_off</span>
                                                            </button>
                                                        ) : (
                                                            m.isCustom && (
                                                                <button
                                                                    type="button"
                                                                    onClick={() => handleRemoveCustomRow(m.target)}
                                                                    className="p-1.5 text-slate-400 hover:text-red-400 rounded hover:bg-slate-800 transition-colors"
                                                                    title="Delete custom field"
                                                                >
                                                                    <span className="material-symbols-outlined text-[17px]">delete</span>
                                                                </button>
                                                            )
                                                        )}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        {/* Add Custom Mapping Bar */}
                        <div className="bg-slate-850 border border-slate-700/80 rounded-xl p-3.5 space-y-2">
                            <div className="text-xs font-bold text-slate-300 flex items-center justify-between">
                                <span className="flex items-center gap-1.5">
                                    <span className="material-symbols-outlined text-[16px] text-indigo-400">add_circle</span>
                                    Add Custom Field Mapping
                                </span>
                                <span className="text-[11px] text-slate-400 font-normal">
                                    Target field names follow official QuickBooks or custom attributes
                                </span>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-12 gap-2 items-center">
                                <div className="md:col-span-5">
                                    <SearchableSelect
                                        options={targetSelectOptions}
                                        value={newTarget}
                                        onChange={val => setNewTarget(val)}
                                        placeholder="Search or enter target QB field..."
                                        allowCustom={true}
                                        className="w-full"
                                    />
                                </div>
                                <div className="md:col-span-5">
                                    <SearchableSelect
                                        options={sourceSelectOptions}
                                        value={newSource}
                                        onChange={val => setNewSource(val)}
                                        placeholder="Select source column..."
                                        className="w-full"
                                    />
                                </div>
                                <div className="md:col-span-2 flex justify-end">
                                    <button
                                        type="button"
                                        onClick={handleAddCustomMapping}
                                        disabled={!newTarget.trim() || !newSource.trim()}
                                        className="w-full px-3 py-1.5 bg-indigo-600 hover:bg-indigo-500 text-white rounded text-xs font-semibold disabled:opacity-40 transition-colors"
                                    >
                                        + Add Mapping
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Footer */}
                    <div className="p-4 border-t border-slate-800 bg-slate-950 flex justify-between items-center shrink-0">
                        <div className="text-xs text-slate-400">
                            Source Table: <strong className="text-white">{activeTableName}</strong> &bull; <strong className="text-indigo-300">{activeMappedCount}</strong> active mapping(s)
                        </div>
                        <div className="flex items-center gap-2">
                            {onClose && (
                                <button
                                    type="button"
                                    onClick={onClose}
                                    className="px-4 py-1.5 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-lg text-xs font-semibold transition-colors border border-slate-700"
                                >
                                    Close
                                </button>
                            )}
                            <button
                                type="button"
                                onClick={handleSave}
                                disabled={saving}
                                className="px-5 py-1.5 bg-indigo-600 hover:bg-indigo-500 text-white rounded-lg text-xs font-bold transition-colors flex items-center gap-1.5 shadow-md disabled:opacity-50"
                            >
                                {saving ? (
                                    <>
                                        <span className="material-symbols-outlined text-[15px] animate-spin">sync</span>
                                        Saving...
                                    </>
                                ) : (
                                    <>
                                        <span className="material-symbols-outlined text-[15px]">save</span>
                                        {isStandardMode ? 'Save Standard Preset' : 'Save Field Mappings'}
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ════════════════════════════════════════════════════════════════════════════
                TAB 2: INSPECT & DIFF ANALYZER (Pipeline Mode Only)
            ════════════════════════════════════════════════════════════════════════════ */}
            {pipelineTab === 'diff' && (
                <div className="flex flex-col flex-1 overflow-hidden p-4 space-y-4">
                    {/* Control Bar */}
                    <div className="p-3 bg-slate-850 border border-slate-750 rounded-xl flex flex-wrap items-center justify-between gap-3 text-xs shrink-0">
                        <div className="flex items-center gap-2">
                            <span className="text-slate-400 font-semibold">Inspection Limit:</span>
                            <select
                                value={diffLimit}
                                onChange={e => setDiffLimit(parseInt(e.target.value, 10))}
                                className="bg-slate-900 border border-slate-700 rounded px-2.5 py-1 text-white font-medium focus:outline-none"
                            >
                                <option value={10}>10 records</option>
                                <option value={50}>50 records</option>
                                <option value={100}>100 records</option>
                                <option value={500}>500 records</option>
                            </select>

                            <div className="flex bg-slate-900 p-0.5 rounded-lg border border-slate-750 ml-2">
                                <button
                                    type="button"
                                    onClick={() => setDiffDetailTab('changes')}
                                    className={`px-3 py-1 rounded text-xs font-semibold transition-colors ${diffDetailTab === 'changes' ? 'bg-blue-600 text-white shadow-sm' : 'text-slate-400 hover:text-white'}`}
                                >
                                    Transformations Diff
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setDiffDetailTab('qb_sample')}
                                    className={`px-3 py-1 rounded text-xs font-semibold transition-colors ${diffDetailTab === 'qb_sample' ? 'bg-purple-600 text-white shadow-sm' : 'text-slate-400 hover:text-white'}`}
                                >
                                    QuickBooks Samples
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setDiffDetailTab('pbs_sample')}
                                    className={`px-3 py-1 rounded text-xs font-semibold transition-colors ${diffDetailTab === 'pbs_sample' ? 'bg-sky-600 text-white shadow-sm' : 'text-slate-400 hover:text-white'}`}
                                >
                                    Raw ERP Table
                                </button>
                            </div>
                        </div>

                        <div className="flex items-center gap-2">
                            <button
                                type="button"
                                onClick={handleRunDiff}
                                disabled={loadingDiff}
                                className="px-3.5 py-1.5 bg-slate-750 hover:bg-slate-700 text-slate-200 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition-colors border border-slate-650"
                            >
                                <span className={`material-symbols-outlined text-[15px] ${loadingDiff ? 'animate-spin' : ''}`}>refresh</span>
                                {loadingDiff ? 'Diffing...' : 'Re-Run Diff'}
                            </button>
                            <button
                                type="button"
                                onClick={() => setPipelineTab('sync')}
                                className="px-4 py-1.5 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg text-xs font-bold flex items-center gap-1.5 transition-colors shadow-sm"
                            >
                                <span className="material-symbols-outlined text-[15px]">sync</span>
                                Go to Sync
                            </button>
                        </div>
                    </div>

                    {/* Diff Error Display */}
                    {diffError && (
                        <div className="p-4 rounded-xl border bg-red-950/70 border-red-800 text-red-200 text-xs">
                            <div className="flex items-start gap-2">
                                <span className="material-symbols-outlined text-[20px] text-red-400">error</span>
                                <div>
                                    <h4 className="font-bold text-sm text-red-300">Diff Analysis Failed</h4>
                                    <p className="mt-1 font-mono text-[11px] text-red-200/90">{diffError}</p>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Diff Content Body */}
                    <div className="flex-1 overflow-y-auto space-y-4">
                        {loadingDiff ? (
                            <div className="py-20 text-center text-slate-400 text-xs">
                                <span className="material-symbols-outlined animate-spin text-3xl mb-2 text-blue-400">sync</span>
                                <p>Comparing ERP source records against live QuickBooks Online...</p>
                            </div>
                        ) : diffPreviewData ? (
                            <div className="space-y-4">
                                {/* Statistics Cards */}
                                <div className="grid grid-cols-4 gap-3">
                                    <div className="bg-slate-850 border border-slate-750 rounded-xl p-3 text-center">
                                        <div className="text-xl font-bold text-white">{diffPreviewData.totalPbsRecords ?? 0}</div>
                                        <div className="text-[11px] text-slate-400">ERP Records Evaluated</div>
                                    </div>
                                    <div className="bg-slate-850 border border-emerald-900/60 rounded-xl p-3 text-center">
                                        <div className="text-xl font-bold text-emerald-400">+{diffPreviewData.toCreate?.length ?? 0}</div>
                                        <div className="text-[11px] text-slate-400">New in QuickBooks</div>
                                    </div>
                                    <div className="bg-slate-850 border border-blue-900/60 rounded-xl p-3 text-center">
                                        <div className="text-xl font-bold text-blue-400">{diffPreviewData.toUpdate?.length ?? 0}</div>
                                        <div className="text-[11px] text-slate-400">Existing (Updates)</div>
                                    </div>
                                    <div className="bg-slate-850 border border-purple-900/60 rounded-xl p-3 text-center">
                                        <div className="text-xl font-bold text-purple-300">{diffPreviewData.totalQbRecords ?? 0}</div>
                                        <div className="text-[11px] text-slate-400">Total in QuickBooks</div>
                                    </div>
                                </div>

                                {diffDetailTab === 'changes' && (
                                    <div className="space-y-2">
                                        <h4 className="text-xs font-bold text-slate-300 uppercase tracking-wider">
                                            Transformations & Payload Diff ({diffPreviewData.sampleMappings?.length || 0})
                                        </h4>
                                        <div className="space-y-2">
                                            {(diffPreviewData.sampleMappings || []).map((sample, idx) => (
                                                <div key={idx} className="bg-slate-850 border border-slate-750 rounded-xl p-3 text-xs space-y-2">
                                                    <div className="flex justify-between items-center">
                                                        <div className="flex items-center gap-2">
                                                            <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${sample.action === 'CREATE' ? 'bg-emerald-950 text-emerald-300 border border-emerald-800' : 'bg-blue-950 text-blue-300 border border-blue-800'}`}>
                                                                {sample.action}
                                                            </span>
                                                            <span className="font-mono font-bold text-white">{sample.sourceKey}</span>
                                                            <span className="text-slate-400">&bull;</span>
                                                            <span className="text-slate-200 font-medium">{sample.sourceName}</span>
                                                        </div>
                                                    </div>
                                                    <pre className="p-2.5 bg-slate-950 rounded text-[11px] font-mono text-sky-300 overflow-x-auto border border-slate-800">
                                                        {JSON.stringify(sample.mappedPayload, null, 2)}
                                                    </pre>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {diffDetailTab === 'qb_sample' && (
                                    <div className="space-y-2">
                                        <h4 className="text-xs font-bold text-slate-300 uppercase tracking-wider">
                                            QuickBooks Destination Sample Objects ({diffPreviewData.qbSampleRecords?.length || 0})
                                        </h4>
                                        <pre className="p-3 bg-slate-950 rounded-xl text-[11px] font-mono text-purple-300 overflow-x-auto border border-slate-800 max-h-96">
                                            {JSON.stringify(diffPreviewData.qbSampleRecords || [], null, 2)}
                                        </pre>
                                    </div>
                                )}

                                {diffDetailTab === 'pbs_sample' && (
                                    <div className="space-y-2">
                                        <h4 className="text-xs font-bold text-slate-300 uppercase tracking-wider">
                                            Raw ERP Source Records ({diffPreviewData.pbsSampleRecords?.length || 0})
                                        </h4>
                                        <pre className="p-3 bg-slate-950 rounded-xl text-[11px] font-mono text-sky-300 overflow-x-auto border border-slate-800 max-h-96">
                                            {JSON.stringify(diffPreviewData.pbsSampleRecords || [], null, 2)}
                                        </pre>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className="py-20 text-center text-slate-400 text-xs">
                                <span className="material-symbols-outlined text-4xl mb-2 text-slate-500">compare_arrows</span>
                                <p>Click "Re-Run Diff" above to compare ERP records with QuickBooks Online.</p>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* ════════════════════════════════════════════════════════════════════════════
                TAB 3: SYNC EXECUTION RUNNER (Pipeline Mode Only)
            ════════════════════════════════════════════════════════════════════════════ */}
            {pipelineTab === 'sync' && (
                <div className="flex flex-col flex-1 overflow-hidden p-4 space-y-4">
                    {/* Execution Controls Card */}
                    <div className="p-4 bg-slate-850 border border-slate-750 rounded-xl space-y-4 shrink-0 text-xs">
                        <div className="flex flex-wrap items-center justify-between gap-3">
                            <div>
                                <h4 className="font-bold text-sm text-white flex items-center gap-1.5">
                                    <span className="material-symbols-outlined text-[18px] text-emerald-400">bolt</span>
                                    Batch Synchronization Runner
                                </h4>
                                <p className="text-slate-400 text-xs mt-0.5">
                                    Write records from <strong className="text-slate-200">{config.SourceDatabaseName || 'PBS'} ({activeTableName})</strong> into QuickBooks Online <strong className="text-purple-300">{selectedQBEntity}</strong>.
                                </p>
                            </div>

                            <div className="flex items-center gap-3">
                                <div className="flex items-center gap-1.5">
                                    <span className="text-slate-400">Batch Limit:</span>
                                    <select
                                        value={syncLimit}
                                        onChange={e => setSyncLimit(parseInt(e.target.value, 10))}
                                        className="bg-slate-900 border border-slate-700 rounded px-2.5 py-1 text-white font-medium focus:outline-none"
                                    >
                                        <option value={10}>10 records</option>
                                        <option value={50}>50 records</option>
                                        <option value={100}>100 records</option>
                                        <option value={500}>500 records</option>
                                    </select>
                                </div>

                                <button
                                    type="button"
                                    onClick={() => handleExecuteLiveSync(true)}
                                    disabled={loadingSync}
                                    className="px-3.5 py-1.5 bg-slate-750 hover:bg-slate-700 text-slate-200 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition-colors border border-slate-650"
                                >
                                    <span className="material-symbols-outlined text-[15px]">science</span>
                                    Dry Run (Preview)
                                </button>

                                <button
                                    type="button"
                                    onClick={() => setShowSyncConfirm(true)}
                                    disabled={loadingSync}
                                    className="px-4 py-1.5 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white rounded-lg text-xs font-bold flex items-center gap-1.5 transition-colors shadow-md shadow-emerald-900/30"
                                >
                                    <span className={`material-symbols-outlined text-[15px] ${loadingSync ? 'animate-spin' : ''}`}>
                                        {loadingSync ? 'sync' : 'cloud_upload'}
                                    </span>
                                    {loadingSync ? 'Syncing...' : 'Execute Live Sync'}
                                </button>
                            </div>
                        </div>

                        {/* Confirmation Dialog */}
                        {showSyncConfirm && (
                            <div className="p-3.5 rounded-xl border border-amber-500/40 bg-amber-950/40 text-amber-200 flex flex-wrap items-center justify-between gap-3">
                                <div className="flex items-center gap-2 font-medium">
                                    <span className="material-symbols-outlined text-[20px] text-amber-400">warning</span>
                                    <span>
                                        Write up to <strong>{syncLimit}</strong> records directly to live QuickBooks Online ({selectedQBEntity})?
                                    </span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <button
                                        type="button"
                                        onClick={() => setShowSyncConfirm(false)}
                                        className="px-3 py-1 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded text-xs font-semibold"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => handleExecuteLiveSync(false)}
                                        className="px-4 py-1 bg-emerald-600 hover:bg-emerald-500 text-white rounded text-xs font-bold shadow-sm"
                                    >
                                        Confirm & Run
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Execution Results Banner */}
                    {syncResult && (
                        <div className={`p-4 rounded-xl border text-xs ${
                            syncResult.status === 'SUCCESS' 
                                ? 'bg-emerald-950/60 border-emerald-800 text-emerald-200' 
                                : syncResult.status === 'PARTIAL'
                                    ? 'bg-amber-950/60 border-amber-800 text-amber-200'
                                    : 'bg-red-950/60 border-red-800 text-red-200'
                        }`}>
                            <div className="flex justify-between items-start">
                                <div>
                                    <h4 className="font-bold text-sm flex items-center gap-1.5">
                                        <span className="material-symbols-outlined text-[18px]">
                                            {syncResult.status === 'SUCCESS' ? 'check_circle' : (syncResult.status === 'PARTIAL' ? 'warning' : 'error')}
                                        </span>
                                        Execution Status: {syncResult.status} ({syncResult.durationSeconds !== undefined ? `${syncResult.durationSeconds}s` : `${syncResult.durationMs || 0}ms`})
                                    </h4>
                                    <p className="mt-1">{syncResult.summary}</p>
                                </div>
                                <div className="flex gap-2 text-[11px] font-mono">
                                    <span className="px-2 py-0.5 rounded bg-emerald-900/60 text-emerald-300 border border-emerald-700">
                                        +{syncResult.recordsInserted ?? syncResult.inserted ?? 0} Created
                                    </span>
                                    <span className="px-2 py-0.5 rounded bg-blue-900/60 text-blue-300 border border-blue-700">
                                        {syncResult.recordsUpdated ?? syncResult.updated ?? 0} Updated
                                    </span>
                                    {((syncResult.recordsFailed || 0) > 0 || (syncResult.failed || 0) > 0) && (
                                        <span className="px-2 py-0.5 rounded bg-red-900/60 text-red-300 border border-red-700">
                                            {syncResult.recordsFailed ?? syncResult.failed ?? 0} Failed
                                        </span>
                                    )}
                                </div>
                            </div>

                            {/* Granular Error Diagnostics List */}
                            {syncResult.errors && syncResult.errors.length > 0 && (
                                <div className="mt-3 pt-3 border-t border-red-900/50">
                                    <div className="font-bold text-xs text-red-300 mb-1.5 flex items-center gap-1">
                                        <span className="material-symbols-outlined text-[15px]">report_problem</span>
                                        Error Diagnostics ({syncResult.errors.length} failed record{syncResult.errors.length === 1 ? '' : 's'}):
                                    </div>
                                    <div className="max-h-56 overflow-y-auto space-y-1.5 font-mono text-[11px] pr-1">
                                        {syncResult.errors.map((err, idx) => (
                                            <div key={idx} className="p-2.5 rounded bg-black/40 border border-red-800/80 text-red-200">
                                                <div className="font-semibold text-white flex items-center justify-between">
                                                    <span>Record [{err.key}]{err.name ? ` - ${err.name}` : ''}</span>
                                                    <span className="text-[10px] text-red-400">Failed</span>
                                                </div>
                                                <div className="text-red-300 mt-1 whitespace-pre-wrap">{err.error}</div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Pipeline Info Card */}
                    <div className="p-4 bg-slate-850 border border-slate-750 rounded-xl space-y-3 text-xs">
                        <h4 className="font-bold text-slate-300 uppercase tracking-wider text-[11px]">
                            Pipeline Settings & Configuration Summary
                        </h4>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                            <div className="bg-slate-900 p-2.5 rounded-lg border border-slate-750">
                                <span className="text-slate-400 block text-[10px]">Source Database</span>
                                <strong className="text-white font-mono">{config.SourceDatabaseName || 'pbsdata00'}</strong>
                            </div>
                            <div className="bg-slate-900 p-2.5 rounded-lg border border-slate-750">
                                <span className="text-slate-400 block text-[10px]">ERP Table</span>
                                <strong className="text-amber-300 font-mono">{activeTableName}</strong>
                            </div>
                            <div className="bg-slate-900 p-2.5 rounded-lg border border-slate-750">
                                <span className="text-slate-400 block text-[10px]">Target QuickBooks Entity</span>
                                <strong className="text-purple-300 font-mono">{selectedQBEntity}</strong>
                            </div>
                            <div className="bg-slate-900 p-2.5 rounded-lg border border-slate-750">
                                <span className="text-slate-400 block text-[10px]">Active Mappings</span>
                                <strong className="text-emerald-300 font-mono">{activeMappedCount} columns</strong>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default FieldMappingDesigner;
